"use client";

import React from "react";
import { Coffee, Loader, Send, CircleCheckBig, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
export default function StartYourJourney() {
  return <section data-section-id="223479" id="lets-plan-your-escape" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none opacity-30 dark:opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-bold font-serif text-foreground mb-4 flex items-center justify-center gap-3 tracking-tight">
            Let's Plan Your Escape
            <Coffee className="size-8 md:size-10 text-primary" />
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            Ready to trade the routine for the remarkable? Share your vision with our travel experts, and we'll craft a bespoke itinerary that captures the soul of your next destination.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-2xl border border-border/40 p-6 md:p-10 backdrop-blur-sm">
          <FormProvider formKey="plan_escape_form_start_your_journey" formId="plan_escape_form_start_your_journey" sectionName="Start Your Journey" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          interest: "Luxury Tours",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          interest: {
            required: true
          }
        }} submitText="Begin Exploration" successMessage="Your journey has begun! A VOYAGE specialist will contact you within 24 hours to discuss your escape.">
            <JourneyForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheckBig className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-serif text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] font-default border-primary text-primary hover:bg-primary/5">
          Plan Another Trip
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <FormField control={control} name="interest" rules={{
      required: "Please select your travel style."
    }} render={({
      field
    }) => <FormItem className="flex flex-col items-center space-y-4 pb-6 border-b border-border/50">
            <FormLabel className="text-lg font-semibold font-default text-foreground tracking-wide">I'm looking for:</FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value} onValueChange={value => {
          if (value) field.onChange(value);
        }} className="flex flex-wrap justify-center gap-2" variant="outline" size="lg">
                <ToggleGroupItem value="Luxury Tours" aria-label="Luxury Tours" className="px-6 font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-full">
                  Luxury
                </ToggleGroupItem>
                <ToggleGroupItem value="Adventure" aria-label="Adventure" className="px-6 font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-full">
                  Adventure
                </ToggleGroupItem>
                <ToggleGroupItem value="Cultural" aria-label="Cultural" className="px-6 font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-full">
                  Culture
                </ToggleGroupItem>
                <ToggleGroupItem value="Relaxation" aria-label="Relaxation" className="px-6 font-default data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-full">
                  Relax
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Alexander Vance" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+1 (555) 000-0000" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Email Address</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="explorer@voyage.com" className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Destination Dreams (Optional)</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Tell us about your dream destinations, preferred dates, or specific interests..." className="min-h-[120px] resize-y font-default focus-visible:ring-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium font-default">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 font-default font-semibold tracking-wide rounded-full bg-primary hover:bg-primary/90 transition-transform active:scale-95 shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Mapping Your Route...
          </> : <>
            Begin Exploration
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}